/*****************************************************
Naam: useEmailQueue.js
Locatie: src/components/Dashboard/hooks/useEmailQueue.js
Datum: 2026-02-07
Versie: 1.0.0
******************************************************
Beschrijving:
Custom hook voor email queue management.
******************************************************/

import { useState, useCallback } from 'react';
import { mapParsedToEmailData } from '../utils/dashboardHelpers';

export const useEmailQueue = () => {
  const [emailQueue, setEmailQueue] = useState([]);
  const [emailData, setEmailData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewEmail, setPreviewEmail] = useState(null);

  const loadEmailQueue = useCallback(async () => {
    try {
      const response = await fetch('/api/email-queue');
      const data = await response.json();
      if (data.success) {
        setEmailQueue(data.queue);
      }
    } catch (error) {
      console.error('❌ Error loading email queue:', error);
    }
  }, []);

  const moveEmailToDashboard = async (uid) => {
    if (!uid) return false;
    
    try {
      const moveResponse = await fetch('/api/email-monitor/move-imported', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uids: [uid] })
      });
      
      if (moveResponse.ok) {
        return true;
      }
      return false;
    } catch (error) {
      console.warn('⚠️ Fout bij verplaatsen email:', error);
      return false;
    }
  };

  const removeFromQueue = async (emailId) => {
    try {
      await fetch(`/api/email-queue/${emailId}`, { method: 'DELETE' });
      loadEmailQueue();
    } catch (error) {
      console.error('❌ Error removing from queue:', error);
    }
  };

  const handleCreateAanvraagFromQueue = useCallback(async (email) => {
    
    if (email.extractedData) {
      setEmailData(email.extractedData);
    }
    
    if (email.uid) {
      await moveEmailToDashboard(email.uid);
    }
    
    await removeFromQueue(email.id);
    
    return 'aanvraag'; // Return modal type to open
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteEmailFromQueue = useCallback(async (emailId) => {
    const email = emailQueue.find(e => e.id === emailId);
    
    if (email?.uid) {
      await moveEmailToDashboard(email.uid);
    }
    
    await removeFromQueue(emailId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailQueue]);

  const handlePreviewEmail = useCallback((email) => {
    setPreviewEmail(email);
    setShowPreview(true);
  }, []);

  const handleImportFromPreview = useCallback(async (emailDataInput) => {
    setShowPreview(false);
    
    if (emailDataInput.uid) {
      await moveEmailToDashboard(emailDataInput.uid);
    }
    
    if (emailDataInput.id) {
      await removeFromQueue(emailDataInput.id);
    }
    
    const parsed = emailDataInput.extractedData?.fullParsed || emailDataInput.extractedData;
    
    if (parsed) {
      const mappedData = mapParsedToEmailData(parsed, 'email');
      setEmailData(mappedData);
    }
    
    return 'aanvraag'; // Return modal type to open
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleWhatsAppImport = useCallback((parsedData) => {
    const mappedData = mapParsedToEmailData(parsedData, 'whatsapp');
    setEmailData(mappedData);
    return 'aanvraag'; // Return modal type to open
  }, []);

  const clearEmailData = useCallback(() => {
    setEmailData(null);
  }, []);

  return {
    emailQueue,
    emailData,
    showPreview,
    previewEmail,
    loadEmailQueue,
    setShowPreview,
    clearEmailData,
    handleCreateAanvraagFromQueue,
    handleDeleteEmailFromQueue,
    handlePreviewEmail,
    handleImportFromPreview,
    handleWhatsAppImport
  };
};

export default useEmailQueue;
